import React, { useState, useRef } from 'react';
import { motion, useMotionValue, useTransform } from 'framer-motion';
import { ArrowRight, Clock, Zap, Globe } from 'lucide-react';
const ArrowSlideButton = ({
  children,
  onClick
}: {
  children: React.ReactNode;
  onClick: () => void;
}) => {
  const [isHovered, setIsHovered] = useState(false);
  return <motion.button className="relative group px-8 py-4 bg-white hover:bg-gray-100 text-gray-900 rounded-lg font-medium transition-colors duration-300 flex items-center font-sans" onClick={onClick} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} whileHover={{
    scale: 1.02
  }} whileTap={{
    scale: 0.98
  }} animate={{
    boxShadow: ["0 4px 20px rgba(255, 255, 255, 0.2)", "0 8px 30px rgba(255, 255, 255, 0.3)", "0 4px 20px rgba(255, 255, 255, 0.2)"]
  }} transition={{
    boxShadow: {
      duration: 3,
      repeat: Infinity,
      ease: "easeInOut"
    }
  }} data-id="element-109">
      <motion.span className="relative z-10" animate={{
      x: isHovered ? -4 : 0
    }} transition={{
      duration: 0.3,
      ease: "easeInOut"
    }} data-id="element-110">
        {children}
      </motion.span>
      
      <motion.div className="overflow-hidden flex items-center justify-center" animate={{
      width: isHovered ? 24 : 0,
      opacity: isHovered ? 1 : 0,
      marginLeft: isHovered ? 10 : 0
    }} transition={{
      duration: 0.3,
      ease: "easeInOut"
    }} data-id="element-111">
        <ArrowRight className="w-5 h-5 flex-shrink-0" data-id="element-112" />
      </motion.div>
    </motion.button>;
};
const StatisticItem = ({
  icon: Icon,
  value,
  label,
  delay
}: {
  icon: React.ElementType;
  value: string;
  label: string;
  delay: number;
}) => {
  return <motion.div className="flex flex-col items-center text-center group" initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.6,
    delay,
    ease: "easeOut"
  }} data-id="element-113">
      <motion.div className="w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center mb-3 group-hover:bg-white/20 transition-colors duration-200" whileHover={{
      scale: 1.1
    }} transition={{
      duration: 0.2
    }} data-id="element-114">
        <Icon className="w-6 h-6 text-white" data-id="element-115" />
      </motion.div>
      <div className="font-display text-2xl sm:text-3xl font-bold text-white mb-1" data-id="element-116">
        {value}
      </div>
      <div className="text-sm text-gray-300 leading-tight max-w-[120px]" data-id="element-117">
        {label}
      </div>
    </motion.div>;
};
export function FinalCTA() {
  const containerRef = useRef<HTMLElement>(null);
  const scrollY = useMotionValue(0);
  const [mousePos, setMousePos] = useState({
    x: 0,
    y: 0
  });
  React.useEffect(() => {
    const updateScrollY = () => scrollY.set(window.scrollY);
    window.addEventListener('scroll', updateScrollY);
    return () => window.removeEventListener('scroll', updateScrollY);
  }, [scrollY]);
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setMousePos({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
  };
  const backgroundY = useTransform(scrollY, [0, 500], [0, -50]);
  return <section ref={containerRef} className="relative py-20 sm:py-32 overflow-hidden bg-slate-900 border-t border-slate-800/50" onMouseMove={handleMouseMove} data-id="element-118">
      {/* Clean solid background */}
      
      {/* Canvas texture overlay */}
      <div className="absolute inset-0 opacity-[0.035]" style={{
      backgroundImage: `
            radial-gradient(circle at 50% 50%, #8b7355 1px, transparent 1px),
            radial-gradient(circle at 25% 75%, #8b7355 0.5px, transparent 0.5px),
            radial-gradient(circle at 75% 25%, #8b7355 0.8px, transparent 0.8px)
          `,
      backgroundSize: '100px 100px, 150px 150px, 80px 80px',
      backgroundPosition: '0 0, 20px 20px, 40px 40px'
    }} data-id="element-119" />
      

      <div className="container relative z-10" data-id="element-120">
        {/* Statistics Row */}
        <motion.div className="flex justify-center items-center gap-8 sm:gap-16 mb-16" initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        ease: "easeOut"
      }} data-id="element-121">
          <StatisticItem icon={Clock} value="5 min" label="saved per email" delay={0.2} data-id="element-122" />
          <StatisticItem icon={Zap} value="2 min" label="response time" delay={0.4} data-id="element-123" />
          <StatisticItem icon={Globe} value="24/7" label="availability" delay={0.6} data-id="element-124" />
        </motion.div>

        {/* Main Content */}
        <div className="text-center max-w-4xl mx-auto" data-id="element-125">
          {/* Main Heading */}
          <motion.h2 className="font-display text-5xl sm:text-6xl md:text-7xl font-semibold text-white mb-6 tracking-tight leading-[0.9]" initial={{
          opacity: 0,
          y: 40
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.8,
          ease: "easeOut"
        }} data-id="element-126">
            Ready to reclaim your day?
          </motion.h2>

          {/* Subheading */}
          <motion.p className="text-xl sm:text-2xl text-gray-200 mb-8 leading-relaxed max-w-2xl mx-auto" initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 1.0,
          ease: "easeOut"
        }} data-id="element-127">
            Join us and put your email inbox on automatic
          </motion.p>

          {/* CTA Button */}
          <motion.div className="flex justify-center mb-6" initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 1.2,
          ease: "easeOut"
        }} data-id="element-128">
            <ArrowSlideButton onClick={() => window.location.href = '/waitlist'} data-id="element-129">
              Get Free Early Access
            </ArrowSlideButton>
          </motion.div>

          {/* Supporting Text */}
          <motion.p className="text-sm text-gray-400 flex items-center justify-center gap-2" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.6,
          delay: 1.4
        }} data-id="element-130">
            <span className="w-2 h-2 bg-white/60 rounded-full animate-pulse" data-id="element-131"></span>
            Free during beta • No credit card required
          </motion.p>
        </div>
      </div>
    </section>;
}